import type { SuggestedTag } from "~/ui-rtk/api/paidMediaApi"
import type { TagCategory } from "~/ui-rtk/types/structured-tags"

/**
 * Map Gemini's taxonomy-validated picks (category + option LABELS) onto the
 * composer's selection shape (category slug → option ids). Labels are matched
 * case-insensitively; anything that doesn't resolve is dropped, and a
 * single-select category keeps only its first suggested option.
 */
export function suggestionsToSelection(
  suggestions: SuggestedTag[],
  categories: TagCategory[],
): Record<string, string[]> {
  const selection: Record<string, string[]> = {}
  for (const suggestion of suggestions) {
    const wantedCategory = suggestion.category.trim().toLowerCase()
    const category = categories.find(
      c => c.label.trim().toLowerCase() === wantedCategory,
    )
    if (!category) continue
    const ids = suggestion.options
      .map(label => {
        const wanted = label.trim().toLowerCase()
        return category.options.find(
          o => o.label.trim().toLowerCase() === wanted,
        )?.id
      })
      .filter((id): id is string => !!id)
    if (ids.length === 0) continue
    selection[category.slug] = category.allowMultiple ? ids : [ids[0]]
  }
  return selection
}

/** The tag picks a saved creative analysis left on an asset (`metadata.creativeAnalysis.suggestedTags`). */
export function suggestedTagsOf(
  metadata: Record<string, unknown> | null | undefined,
): SuggestedTag[] {
  const analysis = metadata?.creativeAnalysis as
    | { suggestedTags?: unknown }
    | undefined
  const tags = analysis?.suggestedTags
  if (!Array.isArray(tags)) return []
  return tags.filter(
    (t): t is SuggestedTag =>
      !!t &&
      typeof (t as SuggestedTag).category === "string" &&
      Array.isArray((t as SuggestedTag).options),
  )
}

/**
 * Lay a suggested selection under the user's: a category the user already
 * picked keeps their picks; only empty categories take the suggestion.
 * Returns the merged selection and the slugs that were filled.
 */
export function mergeSuggestedSelection(
  current: Record<string, string[]>,
  suggested: Record<string, string[]>,
): { next: Record<string, string[]>; filled: string[] } {
  const next = { ...current }
  const filled: string[] = []
  for (const [slug, ids] of Object.entries(suggested)) {
    if (ids.length === 0 || (current[slug]?.length ?? 0) > 0) continue
    next[slug] = ids
    filled.push(slug)
  }
  return { next, filled }
}
